import type { CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
  useTokens,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastSafeTextColor } from '../utils/wcagContrast';

/**
 * Main body of the Commercial page. Follows the pet-policy template:
 * a two-column grid of stat cards paired with narrative copy, then a
 * leasing contacts block, a flyer download button, and a cross-link
 * CTA at the bottom pointing residential prospects to floor plans.
 */

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    label: {
      label: 'Address Text',
      type: 'text',
      defaultValue: '17086 SE Rock Creek Blvd, Happy Valley, OR 97015',
    },
    url: {
      label: 'Map URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=17086+SE+Rock+Creek+Blvd+Happy+Valley+OR+97015',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const flyerButtonDef: SlotGroupDefinition = {
  label: 'Download Flyer Button',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Download the Leasing Flyer (PDF)',
    },
    url: {
      label: 'Button URL',
      type: 'url',
      defaultValue:
        'https://media.multihub.io/property/6767906449167749/01a17dbb-7370-754b-975f-7365aa18f42b.pdf',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const floorPlansButtonDef: SlotGroupDefinition = {
  label: 'View Floor Plans Button',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
    url: {
      label: 'Button URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function CommercialDetails() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const footerRef = useScrollReveal<HTMLDivElement>();

  // Pin inline links to the theme's primary color when it clears WCAG AA
  // against the surrounding background; otherwise fall back to solid
  // black/white. Same pattern as PageBlurb.
  const tokens = useTokens();
  const linkColor = contrastSafeTextColor(tokens.color.primary, tokens.color.background);
  const linkStyle: CSSProperties | undefined = linkColor
    ? ({ ['--commercial-link-color' as string]: linkColor } as CSSProperties)
    : undefined;

  const spacesHeading = useContentToken('commercialdetails.spaces_heading');

  const b1Name = useContentToken('commercialdetails.building1_name');
  const b1Size = useContentToken('commercialdetails.building1_size');
  const b1Rate = useContentToken('commercialdetails.building1_rate');
  const b2Name = useContentToken('commercialdetails.building2_name');
  const b2Size = useContentToken('commercialdetails.building2_size');
  const b2Rate = useContentToken('commercialdetails.building2_rate');

  const useTypesLabel = useContentToken('commercialdetails.use_types_label');
  const useTypesValue = useContentToken('commercialdetails.use_types_value');
  const conditionLabel = useContentToken('commercialdetails.condition_label');
  const conditionValue = useContentToken('commercialdetails.condition_value');
  const availabilityLabel = useContentToken('commercialdetails.availability_label');
  const availabilityValue = useContentToken('commercialdetails.availability_value');
  const addressLabel = useContentToken('commercialdetails.address_label');
  const addressLink = useSlotGroup('commercialdetails.address_link', addressLinkDef);

  const locationHeading = useContentToken('commercialdetails.location_heading');

  const contactsHeading = useContentToken('commercialdetails.contacts_heading');

  const broker1Name = useContentToken('commercialdetails.broker1_name');
  const broker1Title = useContentToken('commercialdetails.broker1_title');
  const broker1Phone = useContentToken('commercialdetails.broker1_phone');
  const broker1Email = useContentToken('commercialdetails.broker1_email');
  const broker2Name = useContentToken('commercialdetails.broker2_name');
  const broker2Title = useContentToken('commercialdetails.broker2_title');
  const broker2Phone = useContentToken('commercialdetails.broker2_phone');
  const broker2Email = useContentToken('commercialdetails.broker2_email');

  const flyerButton = useSlotGroup('commercialdetails.flyer_button', flyerButtonDef);
  const floorPlansButton = useSlotGroup('commercialdetails.floorplans_button', floorPlansButtonDef);

  const footerHeading = useContentToken('commercialdetails.footer_heading');

  const stripPhone = (val: string | null): string => (val || '').replace(/[^+\d]/g, '');

  return (
    <>
      <section
        ref={sectionRef}
        /* Keep bottom padding generous (it leads into the primary-colored
           floor-plans band), but trim the top — PageBlurb already provides
           its own bottom padding so a full py-12/md:py-20 up here stacks
           into a very wide empty band above "Available Space". */
        className="reveal-fade-up pt-4 pb-12 md:pt-6 md:pb-20 bg-[var(--vibe-color-background)]"
        style={linkStyle}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* ── Available Space ─────────────────────────── */}
          <EditableToken
            path="commercialdetails.spaces_heading"
            as="h2"
            className="commercial-section-heading"
          >
            {spacesHeading || 'Available Space'}
          </EditableToken>

          <div className="commercial-spaces-table" role="table" aria-label="Available commercial spaces">
            <div className="commercial-spaces-row commercial-spaces-row--head" role="row">
              <span role="columnheader">Space</span>
              <span role="columnheader">Size</span>
              <span role="columnheader">Rate</span>
            </div>
            <div className="commercial-spaces-row" role="row">
              <EditableToken
                path="commercialdetails.building1_name"
                as="span"
                className="commercial-spaces-cell commercial-spaces-cell--name"
              >
                {b1Name || 'Building 1'}
              </EditableToken>
              <EditableToken
                path="commercialdetails.building1_size"
                as="span"
                className="commercial-spaces-cell"
              >
                {b1Size || '±2,329 SF'}
              </EditableToken>
              <EditableToken
                path="commercialdetails.building1_rate"
                as="span"
                className="commercial-spaces-cell"
              >
                {b1Rate || '$22–$25/SF + NNN'}
              </EditableToken>
            </div>
            <div className="commercial-spaces-row" role="row">
              <EditableToken
                path="commercialdetails.building2_name"
                as="span"
                className="commercial-spaces-cell commercial-spaces-cell--name"
              >
                {b2Name || 'Building 2'}
              </EditableToken>
              <EditableToken
                path="commercialdetails.building2_size"
                as="span"
                className="commercial-spaces-cell"
              >
                {b2Size || '±5,018 SF (demisable)'}
              </EditableToken>
              <EditableToken
                path="commercialdetails.building2_rate"
                as="span"
                className="commercial-spaces-cell"
              >
                {b2Rate || '$22–$25/SF + NNN'}
              </EditableToken>
            </div>
          </div>

          <div className="commercial-stats-grid">
            <div className="commercial-stat-card">
              <EditableToken path="commercialdetails.use_types_label" as="span" className="commercial-stat-label">
                {useTypesLabel || 'Use Types'}
              </EditableToken>
              <EditableToken path="commercialdetails.use_types_value" as="span" className="commercial-stat-value">
                {useTypesValue || 'Retail, service, restaurant'}
              </EditableToken>
            </div>
            <div className="commercial-stat-card">
              <EditableToken path="commercialdetails.condition_label" as="span" className="commercial-stat-label">
                {conditionLabel || 'Space Condition'}
              </EditableToken>
              <EditableToken path="commercialdetails.condition_value" as="span" className="commercial-stat-value">
                {conditionValue || 'Vanilla shell'}
              </EditableToken>
            </div>
            <div className="commercial-stat-card">
              <EditableToken path="commercialdetails.availability_label" as="span" className="commercial-stat-label">
                {availabilityLabel || 'Availability'}
              </EditableToken>
              <EditableToken path="commercialdetails.availability_value" as="span" className="commercial-stat-value">
                {availabilityValue || 'Available now'}
              </EditableToken>
            </div>
            <div className="commercial-stat-card">
              <EditableToken path="commercialdetails.address_label" as="span" className="commercial-stat-label">
                {addressLabel || 'Address'}
              </EditableToken>
              <EditableSlotGroup
                groupPath="commercialdetails.address_link"
                definition={addressLinkDef}
              >
                <a
                  href={addressLink.url || '#'}
                  target={addressLink._target || '_blank'}
                  rel={addressLink._target === '_blank' ? 'noopener' : undefined}
                  className="commercial-stat-value commercial-stat-link"
                >
                  {addressLink.label || '17086 SE Rock Creek Blvd, Happy Valley, OR 97015'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* ── The Location ─────────────────────────── */}
          <EditableToken
            path="commercialdetails.location_heading"
            as="h2"
            className="commercial-section-heading commercial-section-heading--spaced"
          >
            {locationHeading || 'The Location'}
          </EditableToken>
          <RichTextContent
            path="commercialdetails.location_bullets"
            as="div"
            className="commercial-bullets"
          />

          {/* ── Leasing Contacts ─────────────────────── */}
          <EditableToken
            path="commercialdetails.contacts_heading"
            as="h2"
            className="commercial-section-heading commercial-section-heading--spaced"
          >
            {contactsHeading || 'Leasing Contacts'}
          </EditableToken>

          <div className="commercial-brokers">
            {/* Broker 1 */}
            <div className="commercial-broker-card">
              <EditableToken
                path="commercialdetails.broker1_name"
                as="h3"
                className="commercial-broker-name"
              >
                {broker1Name || 'Ian M. Biggi'}
              </EditableToken>
              <EditableToken
                path="commercialdetails.broker1_title"
                as="p"
                className="commercial-broker-title"
              >
                {broker1Title || 'Vice President | Licensed in OR & WA'}
              </EditableToken>
              <p className="commercial-broker-contact">
                <a
                  href={broker1Phone ? `tel:${stripPhone(broker1Phone)}` : undefined}
                  className="commercial-broker-link"
                >
                  <EditableToken path="commercialdetails.broker1_phone" as="span">
                    {broker1Phone || '503-222-1683'}
                  </EditableToken>
                </a>
                <span className="commercial-broker-sep" aria-hidden="true"> | </span>
                <a
                  href={broker1Email ? `mailto:${broker1Email}` : undefined}
                  className="commercial-broker-link"
                >
                  <EditableToken path="commercialdetails.broker1_email" as="span">
                    {broker1Email || 'ianbiggi@capacitycommercial.com'}
                  </EditableToken>
                </a>
              </p>
            </div>

            {/* Broker 2 */}
            <div className="commercial-broker-card">
              <EditableToken
                path="commercialdetails.broker2_name"
                as="h3"
                className="commercial-broker-name"
              >
                {broker2Name || 'Michelle D. Rozakis'}
              </EditableToken>
              <EditableToken
                path="commercialdetails.broker2_title"
                as="p"
                className="commercial-broker-title"
              >
                {broker2Title || 'Principal | Licensed in OR'}
              </EditableToken>
              <p className="commercial-broker-contact">
                <a
                  href={broker2Phone ? `tel:${stripPhone(broker2Phone)}` : undefined}
                  className="commercial-broker-link"
                >
                  <EditableToken path="commercialdetails.broker2_phone" as="span">
                    {broker2Phone || '503-222-1195'}
                  </EditableToken>
                </a>
                <span className="commercial-broker-sep" aria-hidden="true"> | </span>
                <a
                  href={broker2Email ? `mailto:${broker2Email}` : undefined}
                  className="commercial-broker-link"
                >
                  <EditableToken path="commercialdetails.broker2_email" as="span">
                    {broker2Email || 'mrozakis@capacitycommercial.com'}
                  </EditableToken>
                </a>
              </p>
            </div>
          </div>

          <RichTextContent
            path="commercialdetails.agency_line"
            as="div"
            className="commercial-agency-line"
          />

          <div className="commercial-actions">
            <EditableSlotGroup
              groupPath="commercialdetails.flyer_button"
              definition={flyerButtonDef}
            >
              <a
                href={flyerButton.url || '#'}
                target={flyerButton._target || '_blank'}
                rel={flyerButton._target === '_blank' ? 'noopener' : undefined}
                className="commercial-cta commercial-cta--filled"
              >
                {flyerButton.label || 'Download the Leasing Flyer (PDF)'}
              </a>
            </EditableSlotGroup>
          </div>

          <RichTextContent
            path="commercialdetails.disclaimer"
            as="p"
            className="commercial-disclaimer"
          />
        </div>
      </section>

      {/* ── Floor Plans cross-link band ─────────────── */}
      {/* Visually styled to match ReviewsCallout: full-bleed primary band,
          large heading in primary-foreground, gradient CTA with hover shimmer. */}
      <section className="bg-[var(--vibe-color-primary)] py-10 md:py-14">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <div ref={footerRef} className="reveal-fade-up">
            <EditableToken
              path="commercialdetails.footer_heading"
              as="h2"
              className="font-heading text-3xl font-normal leading-snug text-[var(--vibe-color-text-on-primary)] sm:text-4xl"
            >
              {footerHeading || 'Looking for an apartment home?'}
            </EditableToken>
            <div className="mt-6">
              <EditableSlotGroup
                groupPath="commercialdetails.floorplans_button"
                definition={floorPlansButtonDef}
              >
                <a
                  href={floorPlansButton.url || '/floor-plans'}
                  target={floorPlansButton._target || '_self'}
                  className="floorplans-cta group/cta relative inline-flex items-center gap-2.5 overflow-hidden px-10 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-[var(--vibe-color-text-on-primary)] transition-all duration-300"
                >
                  <span className="relative z-10">{floorPlansButton.label || 'View Floor Plans'}</span>
                  <svg
                    className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .commercial-section-heading {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.2vw, 2rem);
          font-weight: 400;
          color: var(--vibe-color-text);
        }
        .commercial-section-heading--spaced { margin-top: 3rem; }

        /* Available Space table */
        .commercial-spaces-table {
          display: flex;
          flex-direction: column;
          border: 1px solid var(--vibe-color-border);
          background: #f7f7f8;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 1.5rem;
        }
        .commercial-spaces-row {
          display: grid;
          grid-template-columns: 1.1fr 1fr 1.1fr;
          padding: 0.9rem 1.25rem;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text);
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .commercial-spaces-row:last-child { border-bottom: none; }
        .commercial-spaces-row--head {
          background: var(--vibe-color-background);
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-text-muted);
        }
        .commercial-spaces-cell--name {
          font-family: var(--vibe-font-heading);
          font-weight: 500;
          color: var(--vibe-color-text);
        }
        @media (max-width: 540px) {
          .commercial-spaces-row {
            grid-template-columns: 1fr;
            gap: 0.3rem;
            padding: 0.9rem 1rem;
          }
          .commercial-spaces-row--head { display: none; }
          .commercial-spaces-cell--name { font-size: 1rem; }
        }

        /* 4 stat cards grid */
        .commercial-stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }
        @media (min-width: 768px) {
          .commercial-stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        .commercial-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 0.5rem;
          padding: 1.25rem 1rem;
          border: 1px solid #e8e8ea;
          background: #f7f7f8;
          border-radius: 4px;
          min-height: 100px;
        }
        .commercial-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
        }
        .commercial-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.35;
          color: var(--vibe-color-text-muted);
        }
        .commercial-stat-link {
          color: var(--commercial-link-color, var(--vibe-color-primary));
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .commercial-stat-link:hover {
          opacity: 0.85;
        }

        /* Location bullets */
        .commercial-bullets {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
          padding: 1rem 1.25rem;
          background: #f7f7f8;
          border-left: 3px solid var(--vibe-color-primary);
          border-radius: 2px;
        }
        .commercial-bullets ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0;
        }
        .commercial-bullets li { margin-bottom: 0.4rem; }
        .commercial-bullets li:last-child { margin-bottom: 0; }
        .commercial-bullets a {
          color: var(--commercial-link-color, var(--vibe-color-primary));
          text-decoration: underline;
        }

        /* Brokers */
        .commercial-brokers {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          margin-bottom: 1.25rem;
        }
        @media (min-width: 640px) {
          .commercial-brokers { grid-template-columns: 1fr 1fr; }
        }
        .commercial-broker-card {
          padding: 1.25rem 1.5rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: 4px;
        }
        .commercial-broker-name {
          margin: 0 0 0.35rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.15rem;
          font-weight: 500;
          color: var(--vibe-color-text);
        }
        .commercial-broker-title {
          margin: 0 0 0.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text-muted);
        }
        .commercial-broker-contact {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text);
          word-break: break-word;
        }
        .commercial-broker-link {
          color: var(--commercial-link-color, var(--vibe-color-primary));
          text-decoration: underline;
        }
        .commercial-broker-sep { color: var(--vibe-color-text-muted); }

        .commercial-agency-line {
          margin: 0.25rem 0 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text-muted);
        }
        .commercial-agency-line a {
          color: var(--commercial-link-color, var(--vibe-color-primary));
          text-decoration: underline;
        }

        /* CTAs */
        .commercial-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .commercial-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          padding: 0.75rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          transition: opacity 0.2s;
        }
        .commercial-cta--filled {
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
        }
        .commercial-cta--filled:hover { opacity: 0.85; }

        .commercial-disclaimer {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        /* Floor-plans cross-link CTA — mirrors ReviewsCallout's "tour-cta". */
        .floorplans-cta {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border: 1px solid #000;
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .floorplans-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .floorplans-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .floorplans-cta:hover::before {
          left: 100%;
        }
        .floorplans-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommercialDetails',
  displayName: 'Commercial Details',
  tokens: {
    'commercialdetails.spaces_heading': {
      label: 'Available Space Heading',
      type: 'text',
      defaultValue: 'Available Space',
    },
    'commercialdetails.building1_name': {
      label: 'Building 1 Name',
      type: 'text',
      defaultValue: 'Building 1',
    },
    'commercialdetails.building1_size': {
      label: 'Building 1 Size',
      type: 'text',
      defaultValue: '±2,329 SF',
    },
    'commercialdetails.building1_rate': {
      label: 'Building 1 Rate',
      type: 'text',
      defaultValue: '$22–$25/SF + NNN',
    },
    'commercialdetails.building2_name': {
      label: 'Building 2 Name',
      type: 'text',
      defaultValue: 'Building 2',
    },
    'commercialdetails.building2_size': {
      label: 'Building 2 Size',
      type: 'text',
      defaultValue: '±5,018 SF (demisable)',
    },
    'commercialdetails.building2_rate': {
      label: 'Building 2 Rate',
      type: 'text',
      defaultValue: '$22–$25/SF + NNN',
    },
    'commercialdetails.use_types_label': {
      label: 'Use Types Label',
      type: 'text',
      defaultValue: 'Use Types',
    },
    'commercialdetails.use_types_value': {
      label: 'Use Types Value',
      type: 'text',
      defaultValue: 'Retail, service, restaurant',
    },
    'commercialdetails.condition_label': {
      label: 'Space Condition Label',
      type: 'text',
      defaultValue: 'Space Condition',
    },
    'commercialdetails.condition_value': {
      label: 'Space Condition Value',
      type: 'text',
      defaultValue: 'Vanilla shell',
    },
    'commercialdetails.availability_label': {
      label: 'Availability Label',
      type: 'text',
      defaultValue: 'Availability',
    },
    'commercialdetails.availability_value': {
      label: 'Availability Value',
      type: 'text',
      defaultValue: 'Available now',
    },
    'commercialdetails.address_label': {
      label: 'Address Label',
      type: 'text',
      defaultValue: 'Address',
    },
    'commercialdetails.location_heading': {
      label: 'Location Heading',
      type: 'text',
      defaultValue: 'The Location',
    },
    'commercialdetails.location_bullets': {
      label: 'Location Bullets',
      type: 'richtext',
      defaultValue:
        '<ul><li>Built-in customer base of 174 apartment homes on site</li><li>Visibility from Rock Creek Blvd, with quick access to SE 172nd Ave, SE 82nd Ave, and Johnson Creek Blvd</li><li>Average household income of about $152,000 within 3 miles (2025 estimate)</li><li>Nearby: New Seasons, Fred Meyer, Orangetheory, Grocery Outlet, and Happy Valley Food Carts</li></ul>',
    },
    'commercialdetails.contacts_heading': {
      label: 'Leasing Contacts Heading',
      type: 'text',
      defaultValue: 'Leasing Contacts',
    },
    'commercialdetails.broker1_name': {
      label: 'Broker 1 Name',
      type: 'text',
      defaultValue: 'Ian M. Biggi',
    },
    'commercialdetails.broker1_title': {
      label: 'Broker 1 Title',
      type: 'text',
      defaultValue: 'Vice President | Licensed in OR & WA',
    },
    'commercialdetails.broker1_phone': {
      label: 'Broker 1 Phone',
      type: 'phone',
      defaultValue: '503-222-1683',
    },
    'commercialdetails.broker1_email': {
      label: 'Broker 1 Email',
      type: 'email',
      defaultValue: 'ianbiggi@capacitycommercial.com',
    },
    'commercialdetails.broker2_name': {
      label: 'Broker 2 Name',
      type: 'text',
      defaultValue: 'Michelle D. Rozakis',
    },
    'commercialdetails.broker2_title': {
      label: 'Broker 2 Title',
      type: 'text',
      defaultValue: 'Principal | Licensed in OR',
    },
    'commercialdetails.broker2_phone': {
      label: 'Broker 2 Phone',
      type: 'phone',
      defaultValue: '503-222-1195',
    },
    'commercialdetails.broker2_email': {
      label: 'Broker 2 Email',
      type: 'email',
      defaultValue: 'mrozakis@capacitycommercial.com',
    },
    'commercialdetails.agency_line': {
      label: 'Brokerage Line',
      type: 'richtext',
      defaultValue:
        '<p>Capacity Commercial Group | 805 SW Broadway, Suite 600, Portland, OR 97205 | <a href="https://capacitycommercial.com" target="_blank" rel="noopener">capacitycommercial.com</a></p>',
    },
    'commercialdetails.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        '<p>Information provided by Capacity Commercial Group from sources deemed reliable but not guaranteed.</p>',
    },
    'commercialdetails.footer_heading': {
      label: 'Footer Heading',
      type: 'text',
      defaultValue: 'Looking for an apartment home?',
    },
  },
  slotGroups: {
    'commercialdetails.address_link': addressLinkDef,
    'commercialdetails.flyer_button': flyerButtonDef,
    'commercialdetails.floorplans_button': floorPlansButtonDef,
  },
};

(CommercialDetails as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CommercialDetails;
